'use client'

import type { Chapter, Course } from '@/payload-types'

import { RichText } from '@/components/RichText'
import { getServerSideURL } from '@/utilities/getURL'
import { Image as ImageIcon, Video } from 'lucide-react'
import MuxPlayer from '@mux/mux-player-react'
import { DocumentsBlockComponent } from '@/blocks/Content/DocumentsBlockComponent'

type Props = {
  chapters?: (Course['chapters'][number])[]
  activeChapterIndex?: number
  hideChapterHeader?: boolean
}

type ChapterBlock = Chapter['layout'][number]
type NormalizedChapter = {
  chapter: Chapter
  index: number
}

type VideoLike = {
  muxPlaybackId?: string | null
  title?: string | null
  showTitle?: boolean | null
  caption?: any
}

const renderVideoItem = (
  video: VideoLike | null,
  itemKey: string,
  size: 'default' | 'compact' = 'default'
) => {
  const muxPlaybackId = video?.muxPlaybackId
  const isCompact = size === 'compact'

  return (
    <div className={isCompact ? 'space-y-3' : 'space-y-4'} key={itemKey}>
      {/* Title */}
      {video?.showTitle && video?.title && (
        <h3 className={`font-semibold text-foreground ${isCompact ? 'text-base' : 'text-lg'}`}>
          {video.title}
        </h3>
      )}

      {/* Mux Video Player */}
      {muxPlaybackId ? (
        <div className={`group relative overflow-hidden shadow-xl ring-1 ring-border/30 ${isCompact ? 'rounded-xl shadow-lg' : 'rounded-2xl'}`}>
          <MuxPlayer
            playbackId={muxPlaybackId}
            controls
            autoPlay={false}
            style={{
              display: 'block',
              width: '100%',
              height: 'auto',
              aspectRatio: '16/9'
            }}
            title={video?.title || undefined}
            accentColor="#3b82f6"
            thumbnailTime={0}
          />
        </div>
      ) : (
        <div className={`flex flex-col items-center justify-center border-2 border-dashed border-border/60 bg-muted/20 text-center ${isCompact ? 'rounded-xl p-8' : 'rounded-2xl p-12'}`}>
          <div className={`items-center justify-center rounded-xl bg-muted ${isCompact ? 'mb-3 flex h-12 w-12' : 'mb-4 flex h-16 w-16 rounded-2xl'}`}>
            <Video className={isCompact ? 'h-6 w-6 text-muted-foreground' : 'h-8 w-8 text-muted-foreground'} />
          </div>
          <p className={`font-medium text-muted-foreground ${isCompact ? 'text-xs' : 'text-sm'}`}>
            Voeg een video toe.
          </p>
        </div>
      )}

      {/* Caption (bijschrift) */}
      {video?.caption && (
        <div className="prose-content text-sm text-muted-foreground">
          <RichText data={video.caption} enableGutter={false} enableProse={false} />
        </div>
      )}
    </div>
  )
}

export const CourseLayout = ({ chapters, activeChapterIndex, hideChapterHeader }: Props) => {
  const normalizedChapters: NormalizedChapter[] =
    chapters
      ?.map((chapter, index) => (typeof chapter === 'object' && chapter ? { chapter: chapter as Chapter, index } : null))
      .filter(Boolean) ?? []

  if (!normalizedChapters.length) return null

  const visibleChapters =
    typeof activeChapterIndex === 'number'
      ? normalizedChapters.filter(({ index }) => index === activeChapterIndex)
      : normalizedChapters

  if (!visibleChapters.length) return null

  return (
    <div className="space-y-16">
      {visibleChapters.map(({ chapter, index: chapterIndex }) => {
        const layoutBlocks = chapter.layout ?? []
        const chapterKey = chapter.id ?? `chapter-${chapterIndex}`

        return (
          <section
            className="space-y-4"
            id={chapter.slug || `chapter-${chapterIndex + 1}`}
            key={chapterKey}
          >
            {!hideChapterHeader && (
              <div className="space-y-1">
                <p className="text-xs font-semibold uppercase tracking-widest text-primary">
                  Hoofdstuk {chapterIndex + 1}
                </p>
                <h2 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-[#085e41] to-[#2D5A3D] bg-clip-text text-transparent">{chapter.title}</h2>
              </div>
            )}

            <div className="flex flex-col gap-12">
              {layoutBlocks.map((block, blockIndex) => {
                const blockKey = block.id ?? `${chapterKey}-${blockIndex}`
                return renderBlock(block, blockKey)
              })}
            </div>
          </section>
        )
      })}
    </div>
  )
}

const renderBlock = (block: ChapterBlock, fallbackKey: string) => {
  if (!block) return null

  const key = block.id ?? fallbackKey

  switch (block.blockType) {
    case 'courseRichText':
      return (
        <div className="md:order-[0]" key={key}>
          <RichText
            className="max-w-none"
            data={block.content}
            enableGutter={false}
            enableProse={false}
          />
        </div>
      )

    case 'courseVideo': {
      const media = typeof block.video === 'object' ? (block.video as VideoLike) : null
      return (
        <article key={key}>
          {renderVideoItem(media, key, 'default')}
        </article>
      )
    }

    case 'courseVideoRow': {
      const videoLeft = typeof block.videoLeft === 'object' ? (block.videoLeft as VideoLike) : null
      const videoRight = typeof block.videoRight === 'object' ? (block.videoRight as VideoLike) : null

      return (
        <article className="grid grid-cols-1 gap-6 md:grid-cols-2" key={key}>
          {renderVideoItem(videoLeft, `${key}-left`, 'compact')}
          {renderVideoItem(videoRight, `${key}-right`, 'compact')}
        </article>
      )
    }

    case 'courseImage': {
      type UploadedLike = {
        url?: string | null
        filename?: string | null
        alt?: string | null
        focalX?: number | null
        focalY?: number | null
      }
      const media = typeof block.image === 'object' ? (block.image as UploadedLike) : null
      const serverURL = getServerSideURL()
      const imageURL = media?.url
        ? `${serverURL}${media.url}`
        : media?.filename
          ? `${serverURL}/media/${media.filename}`
          : null
      const focalX = typeof media?.focalX === 'number' ? media.focalX : 50
      const focalY = typeof media?.focalY === 'number' ? media.focalY : 50

      return (
        <figure className="group overflow-hidden rounded-2xl shadow-lg ring-1 ring-border/30 transition-shadow hover:shadow-xl" key={key}>
          {imageURL ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              alt={media?.alt || 'Afbeelding'}
              className="block h-auto w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
              src={imageURL}
              style={{ objectPosition: `${focalX}% ${focalY}%` }}
            />
          ) : (
            <div className="flex flex-col items-center justify-center bg-muted/20 p-12">
              <ImageIcon className="h-12 w-12 text-muted-foreground" />
            </div>
          )}
        </figure>
      )
    }

    case 'courseDocuments': {
      return <DocumentsBlockComponent key={key} {...(block as any)} />
    }

    case 'courseImageText': {
      type UploadedLike = {
        url?: string | null
        filename?: string | null
        alt?: string | null
        focalX?: number | null
        focalY?: number | null
      }
      const media = typeof block.image === 'object' ? (block.image as UploadedLike) : null
      const serverURL = getServerSideURL()
      const imageURL = media?.url
        ? `${serverURL}${media.url}`
        : media?.filename
          ? `${serverURL}/media/${media.filename}`
          : null
      const focalX = typeof media?.focalX === 'number' ? media.focalX : 50
      const focalY = typeof media?.focalY === 'number' ? media.focalY : 50
      const textPosition = block.textPosition || 'left'

      return (
        <article className="course-content overflow-hidden" key={key}>
          <figure
            className={`group overflow-hidden rounded-2xl shadow-lg ring-1 ring-border/30 transition-shadow hover:shadow-xl mb-4 w-full md:w-1/2 ${textPosition === 'right' ? 'md:float-left md:mr-8' : 'md:float-right md:ml-8'}`}
          >
            {imageURL ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                alt={media?.alt || 'Afbeelding'}
                className="block h-auto w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                src={imageURL}
                style={{ objectPosition: `${focalX}% ${focalY}%` }}
              />
            ) : (
              <div className="flex flex-col items-center justify-center bg-muted/20 p-12">
                <ImageIcon className="h-12 w-12 text-muted-foreground" />
              </div>
            )}
          </figure>
          <RichText
            className="max-w-none"
            data={block.content}
            enableGutter={false}
            enableProse={false}
          />
        </article>
      )
    }

    default:
      return null
  }
}
